<template>
  <div :class="['mine-contents', { opened: isColumnOpened }]" >
    <div
      class="mine-contents-header"
      :style="{ 'padding-right': `${scrollWidth}px` }"
    >
      <div class="column-title report">
          <span>
            {{ $t(`mine.column.report`) }}
          </span>
          <button class="sort-button" @click="changeSortBy('title')">
            <i v-if="sort.sortby === 'title' && sort.direction > 0" class="icon icon--spinbox-up"></i>
            <i v-else-if="sort.sortby === 'title' && sort.direction < 0" class="icon icon--spinbox-down"></i>
            <i v-else class="icon icon--spinbox-none"></i>
          </button>
      </div>
      <div class="column-side" :style="{ 'padding-right': `${scrollWidth + 24}px` }">
        <div v-for="(value, key) in columns" :key="key" :class="['column-side-item', key]">
          <span>
            {{ $t(`mine.column.${key}`) }}
          </span>
          <button class="sort-button" @click="changeSortBy(value.sortby)">
            <i v-if="sort.sortby === value.sortby && sort.direction > 0" class="icon icon--spinbox-up"></i>
            <i v-else-if="sort.sortby === value.sortby && sort.direction < 0" class="icon icon--spinbox-down"></i>
            <i v-else class="icon icon--spinbox-none"></i>
          </button>
        </div>
        <button class="toggle-button" @click="isColumnOpened = !isColumnOpened">
          <i class="icon icon--arrow-left-black size-12"></i>
        </button>
      </div>
    </div>
    <div ref="scrollArea" class="mine-contents-body" :class="{'selecting': selectedReport.length}">
      <div
        class="mine-report"
        :class="[
          { analyzing: report.reportStatus === 'ing' },
          { select: findReport(report) > -1 },
          { error: report.isError },
          { 'drag-on': draggingItem.id === report.id }
        ]"
        v-for="report in reportList"
        :key="report.id"
        :data-select="findReport(report) + 1"
        @dragstart="handleDragStart(report)"
        @dragend="$emit('onDragEnd')"
        @touchstart="handleDragStart(report)"
        @touchend="$emit('onDragEnd')"
        >
        <div class="mine-report-item report">
          <MineListTableItem
            :mine-data="report"
            :report-type="report.type"
            :opened="isColumnOpened"
            :origin-status="originStatus(report)"
            :keyword="keyword"
            @retry="retry"
            @changeTitleEditMode="changeTitleEditMode"
          />
          <div class="goto-button-wrap">
            <div class="goto-button" @click="onShowReport(report)">
              <i class="icon icon--arrow-right-blue"></i>
              <span>{{ $t('common.view') }}</span>
            </div>
          </div>
          <div class="footer-status" v-if="report.type !== 'cxTrends'">
            <i class="icon icon--piece"></i>
            <span class="badge" v-if="getDetailCnt(report) > 0">+{{ getDetailCnt(report) }}</span>
            <button type="button" class="more-btn" @click="openDetailPopup(report)">
              {{ $t('common.viewMore') }}
              <i class="icon icon--down-arrow"></i>
            </button>
          </div>
          <div class="footer-status none-status" v-else>
            <span>-</span>
          </div>
        </div>
        <button class="toggle-button" @click="isColumnOpened = !isColumnOpened">
          <i class="icon icon--arrow-left-black size-12"></i>
        </button>
        <div class="unselect-overlay" v-if="selectedReport.length && findReport(report) > -1">
          <button class="close-button" @click="$emit('remove', report)">
            <i class="icon icon--close-size26 size-24"></i>
          </button>
        </div>
      </div>
      <div class="gradient-box"></div>
    </div>
    <div id="mine-ghost" class="ghost"></div>
    <dimmed v-if="isDetailPopup">
      <MineSegmentDetail :mineData="detailData" @onCloseClick="isDetailPopup = false"/>
    </dimmed>
    <ui-loading-circle v-if="isReportLoading" :applyBackground="true"/>
  </div>
</template>

<script>
import MineListTableItem from './MineListTableItem.vue'
import { format, parse } from 'date-fns'
import { HEATMAP_REPORT_LINK, HEATMAP_REPORT_OLD_LINK, SOLOMON_REPORT } from '@/libs/constants'
import { isInIframe } from '@/libs/auth-helper'
import MineSegmentDetail from '@/components/mine/MineSegmentDetail.vue'
import Dimmed from '@/components/ui/Dimmed.vue'
import { createCXReport, getCXReport } from '@/api/hub'
import UiLoadingCircle from '@/components/ui/UiLoadingCircle.vue'

export default {
  name: 'MineListTableView',
  components: { UiLoadingCircle, Dimmed, MineSegmentDetail, MineListTableItem },
  props: {
    reportList: {
      type: Array,
      required: true
    },
    draggingItem: {
      type: Object,
      required: true
    },
    selectedReport: {
      type: Array,
      required: true
    },
    selectedTab: {
      type: String,
      default: 'gpt'
    },
    keyword: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isDetailPopup: false,
      isReportLoading: false,
      detailData: {},
      mashupMinCount: 2,
      isColumnOpened: false,
      sort: {
        sortby: null,
        direction: 0
      },
      columns: {
        type: { name: 'Type', sortby: 'type' },
        url: { name: 'Url', sortby: 'url' },
        period: { name: 'Period', sortby: 'period' },
        device: { name: 'Device', sortby: 'device' }
      },
      scrollWidth: 0
    }
  },
  computed: {
    readyToMashup () {
      return this.selectedReport.length >= this.mashupMinCount
    },
    draggableMap () {
      const map = new Map()
      this.reportList.forEach(report => {
        const isInValidLang =
          this.selectedReport.length === 1 && this.selectedReport[0].lang !== report.lang

        const isSelected = this.findReport(report) > -1

        const isInvalidReport =
          report.isError || report.reportStatus === 'ing' || report.type === 'dashboard' || report.isTitleEditMode

        const isInvalidForMashup =
          this.selectedTab === 'mashup' ? (this.readyToMashup || report.promptType !== 'single' || report.reportType === 'weekly' || report.type === 'target' || isInValidLang) : false

        map.set(report, !isInvalidReport && !isInvalidForMashup && !isSelected)
      })
      return map
    },
    columnList () {
      if (!this.isColumnOpened) {
        return Object.fromEntries(
          Object.entries(this.columns).filter(([key]) =>
            ['report', 'type'].includes(key)
          )
        )
      }
      return this.columns
    }
  },
  watch: {
    reportList () {
      this.checkScroll()
    }
  },
  mounted () {
    this.checkScroll()
  },
  methods: {
    getDetailCnt (report) {
      const segment = ['utm', 'cid', 'ctaDom', 'refer', 'existing', 'hour', 'freq']

      return segment.filter(key => {
        const value = report[key]

        if (key === 'existing') {
          return typeof value === 'boolean' // true/false일 때 count
        }

        return value && value !== 'all'
      }).length
    },
    openDetailPopup (data) {
      this.isDetailPopup = true
      this.detailData = data
    },
    originStatus (report) {
      return {
        isError: report.isError,
        isReportError: report.isReportError,
        isAnalysis: report.reportStatus === 'ing',
        isNew: report.new
      }
    },
    isClickable (report) {
      const isInvalidReport =
        report.isError || report.isReportError || report.reportStatus === 'ing'
      return !isInvalidReport
    },
    handleDragStart (report) {
      this.$emit('onDragStart', report)
      event.dataTransfer.setData('report', JSON.stringify(report))
      const ghostContainer = document.getElementById('mine-ghost')
      ghostContainer.classList.add('visible')
      const cEl = event.target.cloneNode(true)
      ghostContainer.innerHTML = ''
      ghostContainer.appendChild(cEl)
      event.dataTransfer.setDragImage(ghostContainer, 0, 0)
    },
    retry (report) {
      this.$emit('retry', report)
    },
    async onShowReport (report) {
      if (!this.isClickable(report)) {
        return null
      }

      const target = isInIframe() ? '_self' : '_blank'
      const id = report.id || report._id
      if (!id) return

      this.isReportClicking = true
      this.isReportLoading = true

      try {
        if (report.type === 'cxTrends') {
          window.open(
            SOLOMON_REPORT + `/${report.type}/` + id,
            '_blank'
          )
        } else {
          window.open(`${report.isHtml ? HEATMAP_REPORT_LINK : HEATMAP_REPORT_OLD_LINK}/${id}`, target)
        }
      } catch (err) {
        console.error('getCXReport/createCXReport : ', err)
      } finally {
        this.isReportClicking = false
        this.isReportLoading = false
      }
    },
    findReport (report) {
      if (this.selectedReport.length === 0) return -1
      return this.selectedReport.findIndex(item => {
        return item.id === report.id
      })
    },
    setRegDate (utcDate) {
      const date = parse(utcDate, this.timezone)
      return format(date, 'YYYY-MM-DD HH:mm:ss')
    },
    changeTitleEditMode (reportId, value) {
      const report = this.reportList.find(report => report.id === reportId)
      if (report) {
        this.$set(report, 'isTitleEditMode', value)
      }
    },
    changeSortBy (sortby) {
      if (this.sort.sortby === sortby && this.sort.direction > 0) {
        this.sort = { sortby, direction: -1 }
      } else {
        this.sort = { sortby, direction: 1 }
      }

      this.$emit('onSort', this.sort)
    },
    checkScroll () {
      this.$nextTick(() => {
        const scrollAreaEl = this.$refs.scrollArea
        if (scrollAreaEl) this.scrollWidth = scrollAreaEl.offsetWidth - scrollAreaEl.clientWidth
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$analyzeColor: #b65efc;

.mine-contents {
  padding: 0 36px;
  height: calc(100% - 143px);
  & * {
    box-sizing: border-box;
  }
  .toggle-button {
    display: flex;
    position: absolute;
    padding: 8px;
    background-color: #fff;
    border-radius: 100%;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.2);
    z-index: 1;
    &:hover {
      background-color: #f4f4f4;
    }
    &:active {
      background-color: #e6e6e6;
    }
  }

  &-header {
    position: relative;
    display: flex;
    width: 100%;
    height: 52px;
    margin-bottom: 1px;
    color: #fff;
    font-size: 13px;
    background-color: #555;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    overflow: hidden;

    & * {
      transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
    }
    .column-title {
      display: flex;
      align-items: flex-end;
      gap: 5px;
      flex: 1;
      padding: 6px 35px;
      border-top-left-radius: 10px;
      background-color: #333;
      span {
        line-height: 18px;
      }
      .sort-button {
        display: flex;
        .icon {
          display: block;
        }
      }
    }
    .column-side {
      position: absolute;
      right: 0;
      display: flex;
      align-items: flex-end;
      gap: 6px;
      height: 100%;
      padding: 0 24px 6px 0;
      background: #555;
      transform: translateX(423px);
      .column-side-item {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        box-sizing: initial;
        &.type {
          width: 75px;
          padding-left: 24px;
          transform: translateX(20px);
        }
        &.url {
          width: 249px;
          padding: 0 15px;
          transform: translateX(236px);
        }
        &.period {
          width: 91px;
          padding: 0 12px;
          transform: translateX(200px);
        }
        &.device {
          width: 50px;
          padding: 0 253px 0 12px;
        }
      }
      .sort-button {
        .icon {
          display: block;
        }
      }
      .toggle-button {
        left: 0;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }
  &-body {
    position: relative;
    max-height: calc(100% - 77px);
    background: #fff;
    border-bottom: 26px solid #fff;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2));

    .gradient-box {
      content: '';
      position: sticky;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 16px;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%);
      z-index: 1;
    }
    &.selecting {
      .mine-report {
        &[draggable=false] {
          &:not(.select) {
            opacity: 0.3;
            pointer-events: none;
            /deep/ .mine-card {
              * {
                opacity: 1;
              }
              border-color: #bcbcbc;
              .mine-card-body {
                &:before {
                  border-color: #1c1c1c;
                  background: linear-gradient(
                    270deg,
                    rgba(0, 0, 0, 0.5) 0%,
                    rgba(0, 0, 0, 0) 100%
                  );
                }
              }
            }
          }
        }
      }
    }
    .mine-report {
      position: relative;
      padding: 0 24px;
      .toggle-button {
        display: none;
        right: 396px;
        top: 50%;
        transform: translate(-50%, -50%);
        transition: all 360ms cubic-bezier(0.68, 0, 0.47, 1);
        box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 5px 6px -2px rgba(0, 0, 0, 0.16);
        z-index: 2;
      }

      .goto-button-wrap {
        position: absolute;
        top: 5px;
        right: 0;
        width: 115px;
        height: calc(100% - 10px);
        background-color: #FFF;
        z-index: 1;
        &:after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          transform: translateX(100%);
          width: 24px;
          height: 100%;
          background-color: white;
        }
        .goto-button {
          position: absolute;
          top: 50%;
          left: 15px;
          transform: translateY(-50%);
          @include flexXYcenter;
          height: 32px;
          padding: 0 12px 0 10px;
          gap: 4px;
          border-radius: 5px;
          border: 1px solid #0085FF;
          background-color: #FFF;
          color: #0085FF;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          z-index: 2;
          transition: none;
          line-height: 1;
          &:hover {
            transition: none;
            background-color: #0085FF;
            color: #fff;
            .icon {
              filter: brightness(0) invert(1);
            }
          }
        }
      }

      .footer-status {
        z-index: 1;
        position: absolute;
        height: calc(100% - 10px);
        top: 5px;
        right: 115px;
        padding: 16px;
        display: flex;
        align-items: center;
        gap: 4px;
        justify-content: flex-end;
        background-color: #fff;
        &:lang(en),&:lang(ja) {
          padding: 0;
        }

        &.none-status {
          color:  #444;
          font-size: 13px;
          font-weight: 400;
          line-height: 100%;
          min-width: 111px;
          justify-content: center;
          span {
            padding-right: 18px;
          }
        }

        .badge {
          border-radius: 3px;
          background: #EC0047;
          padding: 2px 4px;
          color: #FFF;
          font-size: 10px;
          font-weight: 500;
        }

        .more-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #444;
          font-size: 13px;
          font-weight: 400;
          line-height: 100%;
          padding: 4px 5px;
          border-radius: 3px;
          transition: background-color .3s ease-in-out;

          &:hover {
            background-color: #F4F4F4;
          }
        }
      }

      &.select {
        .mine-report-item, .mine-report-mashup {
          pointer-events: none;
        }
        &:after {
          content: attr(data-select);
          position: absolute;
          top: 5px;
          left: 5px;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: 1px solid #fff;
          background: $brandColor;
          box-shadow: 0px 1px 1px 0px rgba(0, 0, 0, 0.12);

          color: #fff;
          padding: 3px;

          text-align: center;
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
          box-sizing: border-box;
          z-index: 2;
        }
        &:before {
          content: '';
          position: absolute;
          top: 0px;
          left: 0px;
          width: calc(100% - 26px);
          height: calc(100% - 5px);
          box-sizing: content-box;
          border: 2px solid $brandColor;
          border-radius: 10px;
          z-index: 2;
          pointer-events: none;
        }
        .unselect-overlay {
          display: none;
        }
        &:hover {
          .unselect-overlay {
            position: absolute;
            top: 3px;
            left: 3px;
            width: calc(100% - 28px);
            height: calc(100% - 7px);
            background: rgba(0, 0, 0, 0.60);
            border-radius: 8px;
            cursor: default;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            z-index: 2;
            .close-button {
              display: flex;
              padding: 8px;
              margin: 29px;
            }
          }
        }
      }
      &.error, &.analyzing {
        .goto-button-wrap {
          .goto-button {
          opacity: 0.3;
          pointer-events: none;
          }
        }
      }
      &[draggable='true'] {
        cursor: move;
        &:hover {
          &:before {
            content: '';
            position: absolute;
            top: 0px;
            left: 0px;
            width: calc(100% - 26px);
            height: calc(100% - 5px);
            box-sizing: content-box;
            border: 2px solid #0085ff;
            border-radius: 10px;
            z-index: 2;
            pointer-events: none;
          }
          &:after {
            content: '';
            position: absolute;
            left: -7px;
            top: 50%;
            transform: translate(-100%, -50%);
            background-image: url('../../assets/img/mine/icon--dragndrop.svg');
            background-size: cover;
            width: 10px;
            height: 14px;
          }
        }
      }
      &:hover {
        &:not(.select) {
          .mine-report-item {
            background-color: #f4f4f4;
            .goto-button-wrap {
              background-color: #f4f4f4;
            }
            .footer-status {
              background-color: #f4f4f4;
            }
          }
          .mine-report-mashup {
            background-color: #f4f4f4;
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
            .mine-mashup-header {
              display: flex;
            }
            .goto-button-wrap {
              background-color: #f4f4f4;
            }
          }
        }
        &.analyzing, &.error {
          .mine-report-mashup {
            .mine-mashup-header {
              background-color: #777;
            }
          }
        }
        &:not(.select) {
          .toggle-button {
            display: flex;
          }
        }
      }
    }
    &.drag-on {
      opacity: 0.72;
    }
  }
  .mine-report-item {
    position: relative;
    &.report {
      width: 100%;
    }
    .no-report-contents {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #767676;
      font-size: 13px;
      font-weight: 500;
      line-height: 1.5;
      max-width: 130px;
      word-wrap: break-word;
    }
  }
  .mine-report-mashup {
    position: relative;
    .icon--filing-iron {
      position: absolute;
      top: 50%;
      left: -4px;
      transform: translate(-100%, -50%);
    }
    &:after {
      content: '';
      position: absolute;
      top: 0px;
      left: -24px;
      width: calc(100% + 22px);
      height: calc(100% - 5px);
      box-sizing: content-box;
      border: 2px solid #333;
      border-radius: 10px;
      z-index: 1;
      pointer-events: none;
    }
    .mine-mashup-header {
      display: none;
      position: absolute;
      left: 6px;
      top: 50%;
      transform: translateY(-50%);
      align-items: center;
      gap: 8px;
      padding: 1px 8px;
      background-color: #333;
      color: #fff;
      border-radius: 4px;
      z-index: 2;

      & .mashup-title-wrap {
        .title {
          font-size: 13px;
          font-weight: 600;
        }
      }
      & .label-list {
        display: flex;
        gap: 0;
      }
      & .lang {
        position: relative;
        display: flex;
        width: 44px;
        height: 20px;
        padding: 4px 2px;
        margin-right: 8px;
        justify-content: center;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
        border-radius: 4px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        background: #fff;
        &:after {
          position: absolute;
          top: 50%;
          right: -9px;
          content: '';
          width: 1px;
          height: 8px;
          background: #fff;
          transform: translateY(-50%);
          opacity: 0.2;
        }

        & .lang-text {
          text-transform: uppercase;
          color: #666;
          text-align: center;
          font-size: 12px;
          font-style: normal;
          font-weight: 600;
          line-height: 100%;
          display: inline-block;
        }
      }

      & .new {
        margin-left: 8px;
      }

      & .separator {
        display: inline-block;
        width: 1px;
        height: 8px;
        opacity: 0.2;
        margin: 0 4px;
        background-color: #fff;
      }

      & > div {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .state-label {
        position: relative;
        display: inline-block;
        color: #fff;
        text-align: center;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        padding: 4px 6px;
        vertical-align: baseline;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.8);
      }
      .date-view-info {
        color: #bbb;
        font-size: 12px;
        font-weight: 500;
        line-height: 1;
      }
    }
    .retry-button {
      position: absolute;
      top: 50%;
      left: calc((100% - 200px)/2);
      transform: translate(-50%, -50%);
      z-index: 3;
    }
  }
  &.opened {
    .mine-contents-header {
      .column-side {
        transform: translateX(0);
        .column-side-item {
          &.type {
            transform: translateX(0);
          }
          &.url {
            padding: 0 15px;
            transform: translateX(0);
          }
          &.period {
            transform: translateX(0);
          }
        }
        .toggle-button {
          .icon {
            rotate: 180deg;
          }
        }
      }
    }
    .mine-contents-body {
      .mine-report {
        .toggle-button {
          transform: translate(-423px, -50%) translateX(-50%);
          .icon {
            rotate: 180deg;
          }
        }
        .retry-button {
          right: calc(726px / 2);
          left: auto;
          transform: translate(50%, -50%);
        }
      }
    }
  }
  .ghost {
    display: block;
    position: absolute;
    top: 0;
    right: 100%;
    z-index: -99;
    width: 800px;
    .mine-report-item, .mine-report-mashup {
      opacity: .7;
      transform-origin: top left;
      transform: scale(0.8);
      border-radius: 10px;
      border: none;
      background-color: #fff;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06), 0 18px 22px -7px rgba(0, 0, 0, 0.16);
      overflow: hidden;
      .mine-report-item {
        opacity: 1;
        transform: scale(1);
      }
      &:after {
        display: none;
      }
    }
    .toggle-button {
      display: none;
    }
    .goto-button-wrap, .footer-status {
      display: none;
    }
    &.visible {
      display: block;
    }
  }
}
</style>
